// figureNodeView.js
// ProseMirror NodeView for the single-atom `figure` block node.
//
// The figure node is a pure atom — all data (src, caption, classification…) live in
// attrs.  The NodeView renders everything and handles caption editing internally by
// listening to blur/keydown events on the <figcaption> contenteditable and
// dispatching setNodeMarkup transactions to persist the text.
//
// Caption save strategy: persist on blur and Enter, NOT on every input keystroke.
// Dispatching a transaction on every input event causes ProseMirror to refocus its
// own DOM after each update(), which breaks typing in the caption entirely.
//
// DOM structure:
//   <figure class="pm-figure classified classified-*"
//           data-align="left|center|right"
//           style="width: Xpx">
//     <div class="pm-figure-media-wrapper">
//       <img | video | audio>
//     </div>
//     <figcaption class="pm-figure-caption" contenteditable="true">…</figcaption>
//     <div class="pm-figure-resize-handle --left">
//     <div class="pm-figure-resize-handle --right">
//   </figure>

import {setFigureClassification} from '../../helpers/classificationHelpers';
import {pasteAsPlainText} from '../../../../utils/pasteAsPlainText';
import {openAnnotationEditor} from '../../../figure-annotation/AnnotationSession';
import {getAnnotationMerged, observeAnnotation} from '../../yMaps/annotationsYMap';
import {
    applyClassificationToElement,
    isClassificationBadgeClick,
    openClassificationMenuForBlock,
} from '../../../map/classifiedBlockChrome';

const MIN_RESIZE_WIDTH = 60;

function buildMediaEl(attrs) {
    const {src, alt, title, uploading, mediaType} = attrs;
    let el;
    if (mediaType === 'audio') {
        el = document.createElement('audio');
        el.src = src || '';
        el.controls = true;
        if (title) el.title = title;
    } else if (mediaType === 'video') {
        el = document.createElement('video');
        el.src = src || '';
        el.controls = true;
        if (title) el.title = title;
    } else {
        el = document.createElement('img');
        el.src = src || '';
        el.alt = alt || '';
        if (title) el.title = title;
        if (uploading) el.classList.add('image-uploading');
    }
    el.classList.add('pm-figure-media');
    return el;
}

export class FigureNodeView {
    constructor(node, view, getPos, opts = {}) {
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.classificationMenu = opts.classificationMenu || null;
        this._getEditorView = opts.getEditorView || null;
        this._yAnnotations = opts.yAnnotations || null;
        this._currentUser = opts.currentUser || null;

        this._resizeStartX = 0;
        this._resizeStartWidth = 0;
        this._resizeSide = 'right';
        this._resizing = false;
        this._onResizeMove = this._onResizeMove.bind(this);
        this._onResizeUp = this._onResizeUp.bind(this);

        this._overlayImg = null;
        this._unobserveAnnotation = null;

        this._build();
    }

    // ─── Build DOM ────────────────────────────────────────────────────────────

    _build() {
        const attrs = this.node.attrs;

        this.dom = document.createElement('figure');
        this.dom.className = 'pm-figure';
        this.dom.setAttribute('data-align', attrs.align || 'center');
        if (attrs.width) {
            this.dom.style.width = typeof attrs.width === 'number' ? attrs.width + 'px' : attrs.width;
        }
        // Atom node — ProseMirror must not manage any inner content
        this.contentDOM = null;

        this._mediaWrapper = document.createElement('div');
        this._mediaWrapper.className = 'pm-figure-media-wrapper';
        this._mediaEl = buildMediaEl(attrs);
        this._mediaWrapper.appendChild(this._mediaEl);

        // Annotation overlay — stacked above the media, pointer-events:none.
        if (attrs.mediaType === 'image') {
            this._overlayImg = document.createElement('img');
            this._overlayImg.className = 'pm-figure-annotation-overlay';
            this._overlayImg.alt = '';
            this._overlayImg.style.display = 'none';
            this._mediaWrapper.appendChild(this._overlayImg);
        }

        this._buildResizeHandles(attrs.mediaType);
        this.dom.appendChild(this._mediaWrapper);

        // Caption — contenteditable managed by this NodeView (not ProseMirror)
        this._captionEl = document.createElement('figcaption');
        this._captionEl.className = 'pm-figure-caption';
        this._captionEl.contentEditable = 'true';
        this._captionEl.textContent = attrs.caption || '';
        this.dom.appendChild(this._captionEl);

        if (attrs.mediaType === 'image') {
            this._annotateBtn = document.createElement('button');
            this._annotateBtn.className = 'pm-figure-annotate-btn';
            this._annotateBtn.type = 'button';
            this._annotateBtn.textContent = 'Annotate';
            this._annotateBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const {blockId, src} = this.node.attrs;
                if (blockId) openAnnotationEditor({blockId, src, getView: this._getEditorView, currentUser: this._currentUser});
            });
            this.dom.appendChild(this._annotateBtn);

            this._mediaWrapper.addEventListener('dblclick', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const {blockId, src} = this.node.attrs;
                if (blockId) openAnnotationEditor({blockId, src, getView: this._getEditorView, currentUser: this._currentUser});
            });
        }

        // Persist caption on blur and on Enter (not on every keystroke — see file header).
        this._captionEl.addEventListener('blur', this._onCaptionBlur.bind(this));
        this._captionEl.addEventListener('keydown', this._onCaptionKeydown.bind(this));
        this._captionEl.addEventListener('paste', this._onCaptionPaste.bind(this));
        this.dom.addEventListener('mousedown', this._onBadgeClick.bind(this));

        this._applyClassification(attrs);

        if (attrs.mediaType === 'image' && attrs.blockId) {
            this._subscribeAnnotation(attrs.blockId);
        }
    }

    _buildResizeHandles(mediaType) {
        if (mediaType === 'audio') return;
        ['left', 'right'].forEach((side) => {
            const h = document.createElement('div');
            h.className = `pm-figure-resize-handle pm-figure-resize-handle--${side}`;
            h.setAttribute('data-side', side);
            h.addEventListener('mousedown', (e) => this._onResizeStart(e, side));
            this.dom.appendChild(h);
        });
        this._syncResizeHandles(this.node.attrs.align || 'center');
    }

    // left-aligned → right handle only; right-aligned → left handle only; center → both.
    _syncResizeHandles(align) {
        const left = this.dom.querySelector('.pm-figure-resize-handle--left');
        const right = this.dom.querySelector('.pm-figure-resize-handle--right');
        if (!left || !right) return;
        left.style.display  = (align === 'right' || align === 'center') ? '' : 'none';
        right.style.display = (align === 'left'  || align === 'center') ? '' : 'none';
    }

    // ─── Classification badge ─────────────────────────────────────────────────

    _applyClassification(attrs) {
        applyClassificationToElement(this.dom, attrs);
    }

    // ─── Annotation overlay ───────────────────────────────────────────────────

    _subscribeAnnotation(blockId) {
        if (this._unobserveAnnotation) {
            this._unobserveAnnotation();
            this._unobserveAnnotation = null;
        }
        const ya = this._yAnnotations;
        if (!ya) return;
        this._setOverlaySrc(getAnnotationMerged(ya, blockId));
        this._unobserveAnnotation = observeAnnotation(ya, blockId, () => {
            this._setOverlaySrc(getAnnotationMerged(ya, blockId) || '');
        });
    }

    _setOverlaySrc(dataURL) {
        const img = this._overlayImg;
        if (!img) return;
        if (dataURL) {
            img.src = dataURL;
            img.style.display = '';
        } else {
            img.src = '';
            img.style.display = 'none';
        }
    }

    // ─── Classification badge click ───────────────────────────────────────────

    _onBadgeClick(event) {
        if (!isClassificationBadgeClick(event, this.dom)) return;
        if (!this.classificationMenu) return;
        event.preventDefault();
        event.stopPropagation();
        const {classificationLevel, distribution, compartment} = this.node.attrs;
        openClassificationMenuForBlock({
            classificationMenu: this.classificationMenu,
            dom: this.dom,
            attrs: {classificationLevel, distribution, compartment},
            event,
            applyResult: (result) => {
                const pos = this.getPos();
                if (pos == null) return;
                const tr = this.view.state.tr;
                setFigureClassification(tr, pos, result);
                this.view.dispatch(tr);
            },
        });
    }

    // ─── Caption editing ──────────────────────────────────────────────────────

    _saveCaption() {
        const pos = this.getPos();
        if (pos == null) return;
        const caption = this._captionEl.textContent || '';
        if (caption === (this.node.attrs.caption || '')) return;
        const tr = this.view.state.tr.setNodeMarkup(pos, null, {...this.node.attrs, caption});
        this.view.dispatch(tr);
    }

    _onCaptionBlur() {
        this._saveCaption();
    }

    _onCaptionKeydown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            const pos = this.getPos();
            if (pos == null) return;
            const caption = this._captionEl.textContent || '';
            const {state, dispatch} = this.view;
            const nodeSize = this.node.nodeSize;
            const tr = state.tr
                .setNodeMarkup(pos, null, {...this.node.attrs, caption})
                .insert(pos + nodeSize, state.schema.nodes.paragraph.createAndFill());
            tr.setSelection(
                state.selection.constructor.near(tr.doc.resolve(pos + nodeSize + 1))
            );
            dispatch(tr.scrollIntoView());
            this.view.focus();
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            // Revert local text to last-saved value, then return focus to editor
            this._captionEl.textContent = this.node.attrs.caption || '';
            this.view.focus();
        }
    }

    /**
     * Ticket 09 (map-node-dtg-view-audit): strip formatting — only allow plain text in the
     * caption. Mirrors MapNodeChrome.js's identical fix — manually inserts the plain-text
     * payload via the current Selection/Range instead of the deprecated
     * `document.execCommand('insertText', ...)`.
     */
    _onCaptionPaste(e) {
        pasteAsPlainText(this._captionEl, e);
    }

    // ─── Resize ───────────────────────────────────────────────────────────────

    _onResizeStart(e, side) {
        e.preventDefault();
        e.stopPropagation();
        this._resizeStartX = e.clientX;
        this._resizeStartWidth = this.dom.getBoundingClientRect().width || 200;
        this._resizeSide = side;
        this._resizing = true;
        window.addEventListener('mousemove', this._onResizeMove);
        window.addEventListener('mouseup', this._onResizeUp);
    }

    _onResizeMove(e) {
        if (!this._resizing) return;
        const delta = e.clientX - this._resizeStartX;
        const newWidth = Math.max(
            MIN_RESIZE_WIDTH,
            this._resizeSide === 'right'
                ? this._resizeStartWidth + delta
                : this._resizeStartWidth - delta
        );
        this.dom.style.width = newWidth + 'px';
    }

    _onResizeUp() {
        window.removeEventListener('mousemove', this._onResizeMove);
        window.removeEventListener('mouseup', this._onResizeUp);
        if (!this._resizing) return;
        this._resizing = false;
        const newWidth = Math.round(this.dom.getBoundingClientRect().width);
        const pos = this.getPos();
        if (pos == null) return;
        const tr = this.view.state.tr.setNodeMarkup(pos, null, {...this.node.attrs, width: newWidth});
        this.view.dispatch(tr);
    }

    // ─── ProseMirror NodeView interface ───────────────────────────────────────

    update(node) {
        if (node.type !== this.node.type) return false;
        const prev = this.node;
        this.node = node;

        this._applyClassification(node.attrs);

        if (prev.attrs.align !== node.attrs.align) {
            this.dom.setAttribute('data-align', node.attrs.align || 'center');
            this._syncResizeHandles(node.attrs.align || 'center');
        }

        if (prev.attrs.width !== node.attrs.width) {
            const w = node.attrs.width;
            this.dom.style.width = w ? (typeof w === 'number' ? w + 'px' : w) : '';
        }

        const el = this._mediaEl;
        if (el && el.src !== node.attrs.src) el.src = node.attrs.src || '';
        if (el && node.attrs.mediaType === 'image') {
            el.alt = node.attrs.alt || '';
            if (node.attrs.uploading) el.classList.add('image-uploading');
            else el.classList.remove('image-uploading');
        }

        // Only sync caption text when caption is not focused to avoid clobbering the user's in-progress edit.
        const newCaption = node.attrs.caption || '';
        if (prev.attrs.caption !== newCaption && document.activeElement !== this._captionEl) {
            this._captionEl.textContent = newCaption;
        }

        if (prev.attrs.blockId !== node.attrs.blockId && node.attrs.mediaType === 'image') {
            if (node.attrs.blockId) {
                this._subscribeAnnotation(node.attrs.blockId);
            } else {
                if (this._unobserveAnnotation) {
                    this._unobserveAnnotation();
                    this._unobserveAnnotation = null;
                }
                this._setOverlaySrc('');
            }
        }

        return true;
    }

    selectNode() {
        this.dom.classList.add('ProseMirror-selectednode');
    }

    deselectNode() {
        this.dom.classList.remove('ProseMirror-selectednode');
    }

    stopEvent(event) {
        if (this._annotateBtn?.contains(event.target)) return true;
        if (this._captionEl.contains(event.target)) return true;
        // Stop events on resize handle elements themselves (not their children — handles have no children)
        if (event.target?.dataset?.side && event.target.classList.contains('pm-figure-resize-handle')) return true;
        return false;
    }

    ignoreMutation(mutation) {
        if (mutation.target === this._annotateBtn || this._annotateBtn?.contains(mutation.target)) return true;
        if (this._mediaWrapper.contains(mutation.target) || mutation.target === this._mediaWrapper) return true;
        if (this._captionEl.contains(mutation.target) || mutation.target === this._captionEl) return true;
        if (mutation.target?.classList?.contains('pm-figure-resize-handle')) return true;
        if (mutation.target === this._overlayImg) return true;
        return false;
    }

    destroy() {
        window.removeEventListener('mousemove', this._onResizeMove);
        window.removeEventListener('mouseup', this._onResizeUp);
        if (this._unobserveAnnotation) {
            this._unobserveAnnotation();
            this._unobserveAnnotation = null;
        }
    }
}
